import { useAlertContext } from "@shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { tokenUploadApi } from "@/api";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

/**
 * Manages the client's external upload link: reads the active link (null when none is active yet),
 * activates a new one (which also writes an Aktenvermerk server-side), deactivates an active link
 * (which also writes an Aktenvermerk server-side), and copies the link to the clipboard.
 */
export function useClientUploadLink(clientId: string) {
  const { t } = useTranslation();
  const { showSuccess, showError } = useAlertContext();
  const queryClient = useQueryClient();

  const { data: uploadLink = null } = useQuery({
    queryKey: [QueryKey.ActiveUploadLink, clientId],
    queryFn: () => tokenUploadApi.getActiveUploadLink({ clientId }),
    select: (response) => response.url,
  });

  const activate = useMutation({
    mutationFn: () => tokenUploadApi.createUploadLink({ clientId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.ActiveUploadLink, clientId] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.ClientCaseNotes, clientId] });
      showSuccess(t("dropzone.uploadLink.activateSuccess"));
    },
    onError: () => showError(t("dropzone.uploadLink.activateError")),
  });

  const deactivate = useMutation({
    mutationFn: () => tokenUploadApi.deactivateUploadLink({ clientId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.ActiveUploadLink, clientId] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.ClientCaseNotes, clientId] });
      showSuccess(t("dropzone.uploadLink.deactivateSuccess"));
    },
    onError: () => showError(t("dropzone.uploadLink.deactivateError")),
  });

  const copyLink = async () => {
    if (!uploadLink) {
      return;
    }
    try {
      await navigator.clipboard.writeText(uploadLink);
      showSuccess(t("dropzone.uploadLink.linkCopied"));
    } catch {
      showError(t("dropzone.uploadLink.copyFailed"));
    }
  };

  return {
    uploadLink,
    activate,
    deactivate,
    copyLink,
  };
}
